/* eslint-disable unicorn/filename-case -- TODO rename later */
import { clientSDK } from "@istari-frontend/api-client";
import { getEnv } from "@istari-frontend/shared";
import { AssignInfosecLevel, InfosecSchemaTypes, SetPlatformInfosecSettings } from "@istari/istari-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DEFAULT_CACHE_TIME, QUERY_GC_TIME_MS_30M } from "~/constants";
import { userQueryKeys } from "~/user/data-source/user-query-keys";

export const infosecLevelsKeys = {
  all: ["infosecLevels"] as const,
  platformSettings: () => [...infosecLevelsKeys.all, "platformSettings"] as const,
  availableLevels: (schemaType: string) => [...infosecLevelsKeys.all, "available", schemaType] as const,
};

export function useGetPlatformInfosecSettings() {
  return useQuery({
    queryKey: infosecLevelsKeys.platformSettings(),
    queryFn: () => clientSDK.getPlatformInfosecSettings(),
    staleTime: DEFAULT_CACHE_TIME,
    gcTime: QUERY_GC_TIME_MS_30M,
    refetchOnWindowFocus: false,
    enabled: getEnv("VITE_INFOSEC_ENABLED") === "true",
  });
}

export function useListInfosecLevels(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: infosecLevelsKeys.all,
    queryFn: () => clientSDK.listInfosecLevels(),
    staleTime: DEFAULT_CACHE_TIME,
    gcTime: QUERY_GC_TIME_MS_30M,
    refetchOnWindowFocus: false,
    enabled: getEnv("VITE_INFOSEC_ENABLED") === "true" && (options?.enabled ?? true),
  });
}

export function useSetPlatformInfosecSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: {
      enabled: boolean;
      infosecSchemaType: string;
      maxLevel: number;
      defaultResourceLevel: number;
    }) => {
      const setPlatformSettings = new SetPlatformInfosecSettings(
        settings.enabled,
        settings.infosecSchemaType as InfosecSchemaTypes,
        settings.maxLevel,
        settings.defaultResourceLevel,
      );
      return clientSDK.enablePlatformInfosecSettings(setPlatformSettings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: infosecLevelsKeys.platformSettings() });
      queryClient.invalidateQueries({ queryKey: infosecLevelsKeys.all });
    },
  });
}

export function useAssignInfosecLevel(options?: { onSuccess?: () => void; onError?: (error: Error) => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      type: "user" | "model" | "artifact" | "system";
      id: string;
      infosecLevelId: string | null;
    }) => {
      if (!data.infosecLevelId) {
        throw new Error("Infosec level ID is required");
      }

      const assignLevel = new AssignInfosecLevel(data.infosecLevelId);
      return await clientSDK.assignInfosecLevel(data.type, data.id, assignLevel);
    },
    onSuccess: (_data, variables) => {
      switch (variables.type) {
        case "user":
          queryClient.invalidateQueries({ queryKey: ["orgUsers"] });
          queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
          break;
        case "model":
          queryClient.invalidateQueries({ queryKey: ["models", variables.id] });
          queryClient.invalidateQueries({ queryKey: ["models", "list"] });
          queryClient.invalidateQueries({ queryKey: ["artifacts"] });
          break;
        case "artifact":
          queryClient.invalidateQueries({ queryKey: ["artifacts", variables.id] });
          queryClient.invalidateQueries({ queryKey: ["artifacts"] });
          queryClient.invalidateQueries({ queryKey: ["models"] });
          break;
        case "system":
          queryClient.invalidateQueries({ queryKey: ["systems", variables.id] });
          queryClient.invalidateQueries({ queryKey: ["systems"] });
          break;
      }
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
}
